import React, { useId, useMemo, useState, useEffect } from 'react';
import { FiCheckCircle, FiInfo, FiPlus, FiTrash2 } from 'react-icons/fi';
import { formatCents as money } from '../shared/Ui';

const emptyLine = () => ({
  account_id: '',
  debit_cents: '',
  credit_cents: '',
  memo: '',
  party_type: '',
  party_id: '',
  cost_center_id: '',
  project_id: '',
});

function dollarsToCents(v) {
  if (v === '' || v == null) return 0;
  return Math.round(Number(v) * 100);
}

function centsToDollars(c) {
  return ((Number(c) || 0) / 100).toFixed(2);
}

function mapInitialLine(l) {
  return {
    account_id: l.account_id,
    debit_cents: centsToDollars(l.debit_cents),
    credit_cents: centsToDollars(l.credit_cents),
    memo: l.memo || '',
    party_type: l.party_type || '',
    party_id: l.party_id || '',
    cost_center_id: l.cost_center_id || '',
    project_id: l.project_id || '',
  };
}

/**
 * Draft journal line editor with live balance check.
 */
const JournalEditor = ({ accounts = [], initial, onSave, onPost, saving }) => {
  const uid = useId();
  const [postingDate, setPostingDate] = useState(initial?.posting_date || new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState(initial?.memo || '');
  const [lines, setLines] = useState(() => {
    if (initial?.lines?.length) {
      return initial.lines.map(mapInitialLine);
    }
    return [emptyLine(), emptyLine()];
  });

  useEffect(() => {
    if (!initial) return;
    setPostingDate(initial.posting_date || new Date().toISOString().slice(0, 10));
    setMemo(initial.memo || '');
    if (initial.lines?.length) {
      setLines(initial.lines.map(mapInitialLine));
    }
  }, [initial]);

  const totals = useMemo(() => {
    let d = 0;
    let c = 0;
    for (const line of lines) {
      d += dollarsToCents(line.debit_cents);
      c += dollarsToCents(line.credit_cents);
    }
    return { debit: d, credit: c, balanced: d === c && d > 0 };
  }, [lines]);

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const payload = () => ({
    posting_date: postingDate,
    memo,
    lines: lines.map((l) => ({
      account_id: l.account_id,
      debit_cents: dollarsToCents(l.debit_cents),
      credit_cents: dollarsToCents(l.credit_cents),
      memo: l.memo || null,
      party_type: l.party_type || null,
      party_id: l.party_id || null,
      cost_center_id: l.cost_center_id || null,
      project_id: l.project_id || null,
    })),
  });

  const difference = totals.debit - totals.credit;
  const isEmptyEntry = totals.debit === 0 && totals.credit === 0;
  const balanceHint = totals.balanced
    ? 'Debits and credits match — ready to post.'
    : isEmptyEntry
      ? 'Enter amounts on at least two lines to balance this entry.'
      : `Off by ${money(Math.abs(difference))} — add ${difference > 0 ? 'a credit' : 'a debit'} to balance.`;

  const postableAccounts = accounts.filter((a) => !a.is_group);

  return (
    <div className="journal-editor">
      <div className="erp-form-grid">
        <div className="erp-field">
          <label htmlFor={`${uid}-date`}>Posting date</label>
          <input
            id={`${uid}-date`}
            type="date"
            value={postingDate}
            onChange={(e) => setPostingDate(e.target.value)}
          />
        </div>
        <div className="erp-field">
          <label htmlFor={`${uid}-memo`}>Memo</label>
          <input
            id={`${uid}-memo`}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="What is this entry for?"
          />
        </div>
      </div>

      <div className="erp-table-scroll">
        <table className="erp-table erp-journal-table">
          <thead>
            <tr>
              <th scope="col" className="erp-journal-account">Account</th>
              <th scope="col" className="erp-num erp-journal-amount">Debit</th>
              <th scope="col" className="erp-num erp-journal-amount">Credit</th>
              <th scope="col">Memo</th>
              <th scope="col" className="erp-cell-actions">
                <span className="visually-hidden">Remove line</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, idx) => (
              <tr key={idx}>
                <td className="erp-journal-account">
                  <select
                    aria-label={`Account for line ${idx + 1}`}
                    value={line.account_id}
                    onChange={(e) => updateLine(idx, { account_id: e.target.value })}
                  >
                    <option value="">Select…</option>
                    {postableAccounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                    ))}
                  </select>
                </td>
                <td className="erp-journal-amount">
                  <input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="0.00"
                    aria-label={`Debit for line ${idx + 1}`}
                    value={line.debit_cents}
                    onChange={(e) => updateLine(idx, { debit_cents: e.target.value, credit_cents: e.target.value ? '' : line.credit_cents })}
                  />
                </td>
                <td className="erp-journal-amount">
                  <input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="0.00"
                    aria-label={`Credit for line ${idx + 1}`}
                    value={line.credit_cents}
                    onChange={(e) => updateLine(idx, { credit_cents: e.target.value, debit_cents: e.target.value ? '' : line.debit_cents })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Memo for line ${idx + 1}`}
                    value={line.memo}
                    onChange={(e) => updateLine(idx, { memo: e.target.value })}
                  />
                </td>
                <td className="erp-cell-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-icon btn-icon-sm"
                    title="Remove line"
                    aria-label={`Remove line ${idx + 1}`}
                    onClick={() => setLines((p) => p.filter((_, i) => i !== idx))}
                    disabled={lines.length <= 2}
                  >
                    <FiTrash2 aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Totals</td>
              <td className="erp-num">{money(totals.debit)}</td>
              <td className="erp-num">{money(totals.credit)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="erp-actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLines((p) => [...p, emptyLine()])}>
          <FiPlus aria-hidden /> Add line
        </button>
      </div>

      <div
        className={`erp-balance-bar ${totals.balanced ? 'is-balanced' : 'is-pending'}`}
        role="status"
        aria-live="polite"
      >
        <span className="erp-balance-metric">
          <span className="erp-balance-label">Total debits</span>
          <span className="erp-balance-value">{money(totals.debit)}</span>
        </span>
        <span className="erp-balance-metric">
          <span className="erp-balance-label">Total credits</span>
          <span className="erp-balance-value">{money(totals.credit)}</span>
        </span>
        <span className="erp-balance-metric">
          <span className="erp-balance-label">Difference</span>
          <span className="erp-balance-value erp-balance-diff">{money(Math.abs(difference))}</span>
        </span>
        <span className={`erp-balance-status ${totals.balanced ? 'erp-balance-ok' : 'erp-balance-pending'}`}>
          {totals.balanced ? <FiCheckCircle aria-hidden /> : <FiInfo aria-hidden />}
          {totals.balanced ? 'Balanced' : 'Not balanced yet'}
          <span className="visually-hidden"> — {balanceHint}</span>
        </span>
      </div>
      <p className="erp-panel-meta">{balanceHint}</p>

      <div className="erp-actions">
        {onSave && (
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => onSave(payload())}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
        )}
        {onPost && (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={saving || !totals.balanced}
            title={totals.balanced ? undefined : 'Debits and credits must match before posting'}
            onClick={() => onPost(payload())}
          >
            Post
          </button>
        )}
      </div>
    </div>
  );
};

export default JournalEditor;
